import { useState, useRef, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X } from "lucide-react";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddressSuggestion {
  email: string;
  name: string | null;
  lastUsedAt?: string | Date | null;
}

interface ClaimContact {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  contactType?: string | null;
  lastSeenInEmail?: string | null;
}

interface GlobalContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  contactType: string;
  lastSeenInEmail: string | null;
  emailOccurrenceCount: number | null;
}

interface EmailPillInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  suggestions?: AddressSuggestion[];
  onQueryChange?: (query: string) => void;
  claimContacts?: ClaimContact[];
  globalContacts?: GlobalContact[];
  disabled?: boolean;
  "data-testid"?: string;
}

type ContactSource = "Claim Contact" | "Global" | "Prior Recipient";

function mergeAndSort(
  suggestions: AddressSuggestion[],
  claimContacts: ClaimContact[],
  globalContacts: GlobalContact[],
  query: string,
  alreadyAdded: string[],
): Array<{ email: string; label: string; source: ContactSource }> {
  const q = query.toLowerCase();
  const seen = new Set<string>(alreadyAdded.map(e => e.toLowerCase()));
  const merged: Array<{ email: string; label: string; source: ContactSource; ts: number; freq: number }> = [];

  for (const c of claimContacts) {
    if (!c.email) continue;
    const key = c.email.toLowerCase();
    const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
    if (!seen.has(key) && (key.includes(q) || name.toLowerCase().includes(q))) {
      seen.add(key);
      merged.push({
        email: c.email,
        label: name ? `${name} <${c.email}>` : c.email,
        source: "Claim Contact",
        ts: c.lastSeenInEmail ? new Date(c.lastSeenInEmail).getTime() : 0,
        freq: 0,
      });
    }
  }

  for (const g of globalContacts) {
    if (!g.email) continue;
    const key = g.email.toLowerCase();
    const name = [g.firstName, g.lastName].filter(Boolean).join(" ");
    if (!seen.has(key) && (key.includes(q) || name.toLowerCase().includes(q))) {
      seen.add(key);
      merged.push({
        email: g.email,
        label: name ? `${name} <${g.email}>` : g.email,
        source: "Global",
        ts: g.lastSeenInEmail ? new Date(g.lastSeenInEmail).getTime() : 0,
        freq: g.emailOccurrenceCount ?? 0,
      });
    }
  }

  for (const s of suggestions) {
    const key = s.email.toLowerCase();
    if (!seen.has(key) && key.includes(q)) {
      seen.add(key);
      merged.push({
        email: s.email,
        label: s.name ? `${s.name} <${s.email}>` : s.email,
        source: "Prior Recipient",
        ts: s.lastUsedAt ? new Date(s.lastUsedAt as string).getTime() : 0,
        freq: 0,
      });
    }
  }

  return merged
    .sort((a, b) => b.ts - a.ts || b.freq - a.freq)
    .map(({ email, label, source }) => ({ email, label, source }));
}

export function EmailPillInput({
  value,
  onChange,
  placeholder,
  suggestions = [],
  onQueryChange,
  claimContacts = [],
  globalContacts = [],
  disabled = false,
  "data-testid": testId,
}: EmailPillInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryTimerRef = useRef<NodeJS.Timeout>();

  const commitEmail = useCallback((raw: string) => {
    const email = raw.trim();
    if (!EMAIL_REGEX.test(email)) return;
    if (value.map(e => e.toLowerCase()).includes(email.toLowerCase())) return;
    onChange([...value, email]);
    setInputValue("");
    setPopoverOpen(false);
    onQueryChange?.("");
  }, [value, onChange, onQueryChange]);

  const removeEmail = useCallback((idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  }, [value, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter" || e.key === "Tab") {
      if (inputValue.trim()) {
        e.preventDefault();
        commitEmail(inputValue);
      }
    } else if (e.key === "Backspace" && inputValue === "") {
      if (value.length > 0) {
        const last = value[value.length - 1];
        onChange(value.slice(0, -1));
        setInputValue(last);
        setPopoverOpen(false);
      }
    }
  }, [inputValue, value, commitEmail, onChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    const parts = text.split(/[,;\n]+/).map(p => p.trim()).filter(Boolean);
    if (parts.length > 1 || (parts.length === 1 && EMAIL_REGEX.test(parts[0]))) {
      e.preventDefault();
      const valid = parts.filter(p => EMAIL_REGEX.test(p));
      const existing = new Set(value.map(e => e.toLowerCase()));
      const toAdd = valid.filter(p => !existing.has(p.toLowerCase()));
      if (toAdd.length) onChange([...value, ...toAdd]);
    }
  }, [value, onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/,\s*$/, "");
    setInputValue(v);

    // If user typed a comma at end, commit immediately
    if (e.target.value.endsWith(",")) {
      commitEmail(v);
      return;
    }

    clearTimeout(queryTimerRef.current);
    if (v.length >= 2) {
      queryTimerRef.current = setTimeout(() => {
        onQueryChange?.(v);
        setPopoverOpen(true);
      }, 300);
    } else {
      setPopoverOpen(false);
    }
  }, [commitEmail, onQueryChange]);

  const autocompleteItems = inputValue.length >= 2
    ? mergeAndSort(suggestions, claimContacts, globalContacts, inputValue, value)
    : [];

  return (
    <div>
      <Popover open={popoverOpen && autocompleteItems.length > 0} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <div
            data-testid={testId}
            className="flex flex-wrap gap-1 items-center min-h-10 border rounded-md px-3 py-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 bg-background cursor-text"
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            {value.map((email, idx) => (
              <Badge
                key={email}
                variant="outline"
                className="flex items-center gap-1 cursor-pointer"
                data-testid={`pill-${email}`}
              >
                <span
                  className="truncate text-xs max-w-[240px]"
                  title={email}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(idx);
                    setInputValue(email);
                    setTimeout(() => inputRef.current?.focus(), 0);
                  }}
                >
                  {email}
                </span>
                <X
                  className="h-3 w-3 flex-shrink-0 hover:text-destructive"
                  data-testid={`remove-pill-${email}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(idx);
                  }}
                />
              </Badge>
            ))}
            <input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => {
                if (inputValue.trim()) commitEmail(inputValue);
                setTimeout(() => setPopoverOpen(false), 150);
              }}
              placeholder={value.length === 0 ? placeholder : undefined}
              disabled={disabled}
              className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder:text-muted-foreground"
              data-testid={testId ? `${testId}-input` : undefined}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 p-1"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ul className="max-h-48 overflow-y-auto">
            {autocompleteItems.map((item) => (
              <li key={item.email}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent transition-colors flex items-center justify-between gap-2"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitEmail(item.email);
                  }}
                  data-testid={`suggestion-${item.email}`}
                >
                  <span className="truncate">{item.label}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{item.source}</span>
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
      <p className="text-xs text-muted-foreground mt-1">
        Separate multiple addresses with commas or press Enter
      </p>
    </div>
  );
}
